<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-7 mt-4 pt-7">
            <v-row align="center" class="mb-2">
              <v-col cols="12" class="d-flex align-center">
                <h1 class="title-style mr-3">{{ isEdit ? 'Edit Project' : 'Add Project' }}</h1>
                <v-chip
                  v-if="internalProject"
                  color="primary"
                  style="padding: 1em 1em"
                  large
                >
                  Internal Project
                </v-chip>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-tabs v-model="tab">
                  <v-tab :key="PROJECT">
                    Project
                  </v-tab>
                  <v-tab :key="CLIENT">
                    Client
                  </v-tab>
                  <v-tab :key="CONTRACTS">
                    Contracts
                  </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                  <v-tab-item :key="PROJECT">
                    <v-row class="py-5">
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          label="Project Name"
                          v-model="projectName"
                          :rules="[required('Project Name')]"
                          validate-on-blur
                          outlined
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <ProjectStatusDropDownList
                          :id="statusId"
                          :isRequired="true"
                          @change="(newValue) => this.statusId = newValue"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <div class="d-flex align-start">
                          <FormattedNumberField
                            label="Anticipated Revenue"
                            prepend-inner-icon="attach_money"
                            :value="anticipatedRevenue"
                            :readonly="true"
                          ></FormattedNumberField>
                          <v-tooltip left v-if="anticipatedRevenueHint">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                color="warning"
                                class="ml-2 mt-4"
                              >
                                warning
                              </v-icon>
                            </template>
                            <span
                              style="white-space: pre-line; display: inline-block; max-width: 250px;"
                            >
                              {{ anticipatedRevenueHint }}
                            </span>
                          </v-tooltip>
                        </div>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item :key="CLIENT">
                    <v-row class="py-5">
                      <v-col cols="12" sm="6" lg="4">
                        <ClientsDropDownList
                          :isRequired="true"
                          :id="clientId"
                          @clientIdChange="(newValue) => this.clientId = newValue"
                        ></ClientsDropDownList>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item :key="CONTRACTS">
                    <v-row align="center" justify="end" class="mt-3">
                      <v-col cols="12" sm="4" lg="3">
                        <v-btn
                          block
                          outlined
                          color="primary"
                          @click="showAddConsultantModal = true"
                        >
                            Add Consultant +
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row v-if="projectAssignments && projectAssignments.length > 0"
                      align="center" justify="start" class="mt-3"
                    >
                      <v-col cols="12" sm="4" lg="3">
                        <v-select
                          v-model="selectedAssignmentId"
                          :items="assignmentOptions"
                          item-text="label"
                          item-value="value"
                          label="Consultant"
                          outlined
                          dense
                          hide-details
                          style="max-width: 420px; margin-bottom: 0.75em; margin-top: 0.5em;"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="4"
                        lg="3"
                        xl="2"
                      >
                        <v-chip
                          v-if="selectedAssignmentAnticipatedRevenue"
                          color="primary"
                          :small="$vuetify.breakpoint.smAndDown"
                          style="padding: 1.5em 1em; width: 100%; justify-content: center;"
                        >
                          Anticipated Revenue: {{ selectedAssignmentAnticipatedRevenue | currency }}
                        </v-chip>
                        <v-chip
                          v-if="
                            selectedAssignment.endAt &&
                            new Date(selectedAssignment.endAt) <= Date.now() &&
                            selectedAssignmentAnticipatedRevenue === 0
                          "
                          color="primary"
                          outlined
                          small
                          style="padding: 1.8em 1em; width: 100%; justify-content: center;"
                        >
                          Cannot calculate anticipated<br>revenue of finalized contracts
                        </v-chip>
                      </v-col>
                      <v-col cols="12" sm="4" lg="3" class="ml-auto">
                        <!-- Only show delete when a consultant is selected -->
                        <v-btn
                          v-if="selectedAssignment"
                          block
                          color="error"
                          outlined
                          @click="showDeleteConsultantConfirmation = true"
                        >
                          Delete Consultant
                        </v-btn>
                      </v-col>
                    </v-row>
                    <ProjectAssignmentTabs
                      v-if="selectedAssignment"
                      ref="projectAssignmentTabs"
                      :assignment="selectedAssignment"
                      :vmsFee="client.vmsFee"
                    ></ProjectAssignmentTabs>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <ButtonsSubmitForm
      :isFormValid="isFormValid"
      @save="handleSaveProject"
      @cancel="cancel"
      ></ButtonsSubmitForm>
      <!-- Confirmation modal for removing a consultant from the project -->
      <ConfirmationModal
        :showModal="showDeleteConsultantConfirmation"
        :title="'Delete Confirmation'"
        :contentText="'Are you sure you want to remove this consultant from the project?'"
        :toolbarColor="'primary'"
        @cancel-click="showDeleteConsultantConfirmation = false"
        @ok-click="handleDeleteConsultantConfirmed"
      ></ConfirmationModal>
      <DynamicModal
        :showModal="showAddConsultantModal"
        title="Select consultant"
        maxWidth="500px"
      >
        <v-container fluid slot="content">
          <v-form ref="addConsultantForm">
            <v-row class="mt-3">
              <v-col cols="12">
                <UsersDropDownList
                  label="Consultant"
                  :userId="newConsultantUserId"
                  :isRequired="true"
                  @userIdChange="(val) => newConsultantUserId = val"
                  @userNameChange="(firstName, lastName) => newConsultantUserFullName = `${firstName} ${lastName}`.trim()"
                ></UsersDropDownList>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
        <v-container fluid slot="buttons">
          <v-row class="mt-3">
            <v-col cols="6">
              <v-btn
                block
                color="primary"
                @click="handleAddConsultantClick"
              >
                Add
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                outlined
                color="secondary"
                @click="closeAddConsultantModal"
              >
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </DynamicModal>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { projectsRoute } from '@/lib/constants/routes'
import { ButtonsSubmitForm } from '@/components/buttons'
import { required } from '@/lib/formValidations'
import { FormattedNumberField } from '@/components/shared/textFields'
import { DynamicModal, ConfirmationModal } from '@/components/dialogs'
import UsersDropDownList from '@/components/users/UsersDropDownList'
import getDefaultAssignment, { getAssignmentTemplate } from '@/store/modules/models/assignment'
import { ClientsDropDownList } from '@/components/clients'
import ProjectStatusDropDownList from '@/components/projects/ProjectStatusDropDownList'
import ProjectAssignmentTabs from '@/components/projects/ProjectAssignmentTabs'
import { notifyError } from '@/lib/notifications'
import { CONTRACT_TO_HIRE } from '@/lib/constants/contractTypeDefault'
import { CORP_TO_CORP } from '@/lib/constants/employeeTypes'

export default {
  components: {
    ButtonsSubmitForm,
    FormattedNumberField,
    ClientsDropDownList,
    ProjectStatusDropDownList,
    ProjectAssignmentTabs,
    DynamicModal,
    ConfirmationModal,
    UsersDropDownList
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.previousRoute = from
    })
  },
  data: () => ({
    isFormValid: false,
    previousRoute: null,
    tab: null,
    PROJECT: 1,
    CLIENT: 2,
    CONTRACTS: 3,
    selectedAssignmentId: null,
    selectedAssignmentAnticipatedRevenue: 0,
    showAddConsultantModal: false,
    newConsultantUserId: null,
    newConsultantUserFullName: null,
    nextNewAssignmentId: -1,
    showDeleteConsultantConfirmation: false,
    client: { vmsFee: 0 }
  }),
  computed: {
    ...mapFields('projects', [
      'id',
      'projectName',
      'clientId',
      'clientName',
      'statusId',
      'createAt',
      'createdBy',
      'updatedAt',
      'updatedBy',
      'internalProject',
      'anticipatedRevenue',
      'projectAssignments'
    ]),
    ...mapFields('settings', ['payRateSettings']),
    isEdit: {
      get () {
        return !!this.id
      }
    },
    assignmentOptions () {
      if (!this.projectAssignments) return []
      return [...this.projectAssignments]
        .sort((a, b) => (a.userFullName || '').localeCompare(b.userFullName || ''))
        .map(a => ({ label: a.userFullName || 'Unknown', value: a.id }))
    },
    selectedAssignment () {
      if (!this.projectAssignments || this.projectAssignments.length === 0) return null
      return this.projectAssignments.find(a => a.id === this.selectedAssignmentId) ||
        [...this.projectAssignments].sort((a, b) => (a.userFullName || '').localeCompare(b.userFullName || ''))[0]
    },
    anticipatedRevenueInputs () {
      const a = this.selectedAssignment
      if (!a) return null
      return {
        billRateTypeId: a.billRateTypeId,
        billRate: a.billRate,
        workedHoursAverage: a.workedHoursAverage,
        endAt: a.endAt
      }
    },
    assignmentsWithoutEndDate () {
      if (!this.projectAssignments) return []
      return this.projectAssignments.filter(a => !a.endAt)
    },
    assignmentsWithPastEndDate () {
      if (!this.projectAssignments) return []
      return this.projectAssignments.filter(a => a.endAt && new Date(a.endAt) <= Date.now())
    },
    anticipatedRevenueHint () {
      const parts = []

      const missing = this.assignmentsWithoutEndDate
      if (missing.length > 0) {
        const nameList = missing.map(a => a.userFullName || 'Unknown')
        const formattedNames = nameList.length > 1
          ? `${nameList.slice(0, -1).join(', ')} and ${nameList[nameList.length - 1]}`
          : nameList[0]
        const label = missing.length > 1 ? 'consultants' : 'consultant'
        const contractClause = missing.length > 1 ? "their contracts don't have an end date" : "their contract doesn't have an end date"
        parts.push(`Anticipated Revenue cannot be calculated for ${label}: ${formattedNames} since ${contractClause}`)
      }

      const past = this.assignmentsWithPastEndDate
      if (past.length > 0) {
        const nameList = past.map(a => a.userFullName || 'Unknown')
        const formattedNames = nameList.length > 1
          ? `${nameList.slice(0, -1).join(', ')} and ${nameList[nameList.length - 1]}`
          : nameList[0]
        const label = past.length > 1 ? 'consultants' : 'consultant'
        const contractClause = past.length > 1 ? "their contracts' end date has gone past" : "their contract's end date has gone past"
        parts.push(`Anticipated Revenue cannot be calculated for ${label}: ${formattedNames} since ${contractClause}`)
      }

      return parts.join('\n\n')
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler (projectId) {
        this.initializeProject(projectId)
      }
    },
    projectAssignments: {
      immediate: true,
      handler (value) {
        if (!value || value.length === 0) {
          this.selectedAssignmentId = null
          return
        }
        const exists = value.some(a => a.id === this.selectedAssignmentId)
        if (!exists) {
          this.selectedAssignmentId = [...value]
            .sort((a, b) => (a.userFullName || '').localeCompare(b.userFullName || ''))[0].id
        }
      }
    },
    anticipatedRevenueInputs: {
      immediate: true,
      deep: true,
      handler: async function (inputs) {
        if (inputs && inputs.billRateTypeId && inputs.billRate && inputs.workedHoursAverage > 0 && inputs.endAt) {
          this.anticipatedRevenue = await this.getProjectAnticipatedRevenue([...this.projectAssignments])

          if (new Date(this.selectedAssignment.endAt) > Date.now()) {
            this.selectedAssignmentAnticipatedRevenue = await this.getAnticipatedRevenue(
              {
                billRateTypeId: inputs.billRateTypeId,
                billRate: inputs.billRate,
                workedHoursAverage: inputs.workedHoursAverage,
                endDate: inputs.endAt
              }
            )
          } else {
            this.selectedAssignmentAnticipatedRevenue = 0
          }
        } else {
          this.selectedAssignmentAnticipatedRevenue = 0
        }
      }
    },
    clientId (val) {
      this.internalProject = val === 1

      if (this.clientId) {
        this.getClient({ id: this.clientId })
          .then(client => {
            this.client = client
          })
      }
    }
  },
  methods: {
    required,
    ...mapActions('projects', [
      'getProject',
      'saveProject',
      'updateProject',
      'clearProject',
      'deleteProjectAssignment',
      'getAnticipatedRevenue',
      'getProjectAnticipatedRevenue'
    ]),
    ...mapActions('settings', [
      'getSettings'
    ]),
    ...mapActions('client', ['getClient']),
    validateAssignments () {
      const invalidAssignments = (this.projectAssignments || []).filter(a => {
        const isContractToHire = a.contractTypeId === CONTRACT_TO_HIRE
        const isC2C = a.employeeTypeId === CORP_TO_CORP
        return !(
          a.billRate &&
          a.startAt &&
          a.employeeTypeId &&
          a.billRateTypeId &&
          a.payFrequencyId &&
          a.payRateTypeId &&
          a.payRate &&
          a.workedHoursAverage &&
          (isContractToHire ? (a.lengthOfContract !== null && a.conversionFee !== null && a.endAt) : true) &&
          (isC2C ? (a.ein && a.corporateName) : true)
        )
      })

      if (invalidAssignments.length > 0) {
        const names = invalidAssignments.map(a => a.userFullName || 'Unknown')
        const formattedNames = names.length > 1
          ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
          : names[0]
        const label = invalidAssignments.length > 1 ? 'consultants' : 'consultant'
        notifyError(this.$store.dispatch, `Please fill in all required contract fields for ${label}: ${formattedNames}`)
        this.selectedAssignmentId = invalidAssignments[0].id
        this.tab = 2
        this.$nextTick(() => {
          if (this.$refs.projectAssignmentTabs) {
            this.$refs.projectAssignmentTabs.validate()
          }
        })
        return false
      }

      return true
    },
    resetLocalState () {
      this.isFormValid = false
      this.tab = null
      this.selectedAssignmentId = null
      this.selectedAssignmentAnticipatedRevenue = 0
      this.showAddConsultantModal = false
      this.newConsultantUserId = null
      this.newConsultantUserFullName = null
      this.nextNewAssignmentId = -1
      this.showDeleteConsultantConfirmation = false
      this.client = { vmsFee: 0 }
    },
    initializeProject (projectId) {
      this.resetLocalState()

      if (projectId) {
        this.getProject({ id: projectId })
          .then(() => {
            if (this.clientId) {
              this.getClient({ id: this.clientId })
                .then(client => {
                  this.client = client
                })
            }
          })
        return
      }

      this.clearProject()
    },
    leaveProjectEdit () {
      this.resetLocalState()
      this.clearProject()

      if (this.previousRoute && this.previousRoute.name === projectsRoute.name) {
        this.$router.back()
        return
      }

      // Fallback to Vue Router navigation which resets the list filters, sorting, and pagination.
      this.$router.push({ name: projectsRoute.name })
    },
    handleSaveProject () {
      if (this.$refs.form.validate()) {
        if (!this.validateAssignments()) return

        if (this.projectAssignments && this.projectAssignments.length > 0) {
          const action = this.isEdit ? this.updateProject : this.saveProject

          action({
            id: this.id,
            projectName: this.projectName,
            clientId: this.clientId,
            clientName: this.clientName,
            statusId: this.statusId,
            createAt: this.createAt,
            createdBy: this.createdBy,
            updatedAt: this.updatedAt,
            updatedBy: this.updatedBy,
            internalProject: this.internalProject,
            projectAssignments: this.projectAssignments
          }).then(() => {
            this.leaveProjectEdit()
          })
        } else {
          notifyError(
            this.$store.dispatch,
            'Please add at least one consultant to the project.'
          )
        }
      }
    },
    closeAddConsultantModal () {
      this.showAddConsultantModal = false
      this.newConsultantUserId = null
      this.newConsultantUserFullName = null
    },
    handleAddConsultantClick () {
      if (!this.$refs.addConsultantForm.validate()) return
      const newAssignment = getAssignmentTemplate(getDefaultAssignment())
      newAssignment.id = this.nextNewAssignmentId--
      newAssignment.userId = this.newConsultantUserId
      newAssignment.userFullName = this.newConsultantUserFullName
      // Set assignment default values
      newAssignment.workedHoursAverage = this.payRateSettings
        ? this.payRateSettings.workedHoursAverageDefault
        : 173
      newAssignment.overtimeThreshold = this.payRateSettings
        ? this.payRateSettings.overtimeThresholdDefault
        : 40
      newAssignment.overtimeRate = this.payRateSettings
        ? this.payRateSettings.overtimeRateDefault
        : 1.5
      newAssignment.clientId = this.clientId
      this.projectAssignments.push(newAssignment)
      this.selectedAssignmentId = newAssignment.id
      this.closeAddConsultantModal()
      this.$nextTick(() => {
        if (this.$refs.projectAssignmentTabs) {
          this.$refs.projectAssignmentTabs.resetValidation()
        }
      })
    },
    async handleDeleteConsultantConfirmed () {
      const assignment = this.selectedAssignment
      this.showDeleteConsultantConfirmation = false

      if (assignment.id < 0) {
        // Negative ID means the assignment was never saved — remove from local array only
        this.projectAssignments.splice(
          this.projectAssignments.findIndex(a => a.id === assignment.id), 1
        )
      } else {
        // Persisted assignment — send delete request to the backend
        const result = await this.deleteProjectAssignment(assignment.id)
        if (result === 0) {
          // The assignment cannot be deleted since it has time sheet
          // entries.
          notifyError(
            this.$store.dispatch,
            'This consultant cannot be removed since they have time sheet entries associated with this project.'
          )
        }

        // If result is null, the deletion did not succeed
        if (result) {
          this.projectAssignments.splice(
            this.projectAssignments.findIndex(a => a.id === assignment.id), 1
          )
        }
      }
    },
    cancel () {
      this.leaveProjectEdit()
    }
  },
  mounted () {
    this.$refs.form.resetValidation()

    this.getSettings()
  },
  beforeRouteLeave (to, from, next) {
    this.resetLocalState()
    this.clearProject()
    next()
  }
}
</script>
